// Where a floating panel goes, given the trigger it hangs off.
//
// Shared by every picker in the app (combobox, track picker) so they cannot
// drift: one of them used to flip above the trigger 40px sooner than the other,
// and only one of them clamped to the viewport at all.
//
// The rules, in the order they matter:
//
//   * Open below the trigger; flip above when there isn't room below and there
//     is more room above.
//   * Cap the panel's own height to the space on that side, so it scrolls
//     internally rather than running off the screen.
//   * Clamp left/right to an 8px viewport margin.
//   * Clamp `top` against the *bottom* edge as well as the top. Without that
//     last clamp a trigger scrolled out of view drags the panel off-screen with
//     it — the panel is `fixed`, so a trigger at y=2000 puts it at y=2000 too.
//
// Returns pixel numbers; the caller writes them (as `top`, never `bottom`, so
// the clamp above is the only thing deciding the vertical position).
const MARGIN = 8
const GAP = 4
const WANT_HEIGHT = 264
const MIN_HEIGHT = 140
const MIN_WIDTH = 176

export function placeFloatingPanel(trigger, opts = {}) {
  const {
    margin = MARGIN,
    gap = GAP,
    want = WANT_HEIGHT,
    minHeight = MIN_HEIGHT,
    minWidth = MIN_WIDTH,
    maxWidth = Infinity,
  } = opts

  const box = trigger.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.innerHeight

  const below = vh - box.bottom - margin
  const above = box.top - margin
  // Flip only when below is genuinely tight *and* above is roomier — a panel
  // that flips at the first pixel of pressure jumps around while you scroll.
  const up = below < Math.min(want, 180) && above > below

  const room = (up ? above : below) - gap
  const height = Math.min(
    Math.max(minHeight, Math.min(want, room)),
    vh - margin * 2,
  )
  const width = Math.min(
    Math.max(box.width, minWidth),
    Math.min(maxWidth, vw - margin * 2),
  )
  const top = up ? box.top - gap - height : box.bottom + gap

  return {
    left: Math.round(Math.min(Math.max(margin, box.left), vw - width - margin)),
    top: Math.round(Math.min(Math.max(margin, top), vh - height - margin)),
    width: Math.round(width),
    maxHeight: Math.round(height),
  }
}

// Write a placement onto a panel, in viewport coordinates.
//
// `position: fixed` resolves against the viewport *unless* some ancestor has a
// transform, filter, backdrop-filter, contain, perspective or will-change — any
// of which quietly makes it the containing block instead. Then `left: 989px`
// lands wherever 989px from that ancestor happens to be, and a panel that
// measured its trigger correctly still renders in the wrong place.
//
// Rather than police every ancestor (a CSS animation with `forwards` is enough
// to cause it, from a stylesheet nowhere near the picker), the panel is placed,
// measured, and corrected by whatever offset it actually landed with. One extra
// layout read, and placement stops depending on what the surrounding page does.
//
// `top` only, never `bottom` — one axis means the vertical clamp above is the
// single thing deciding where the panel sits.
export function applyFloatingPanel(panel, box) {
  panel.style.bottom = ""
  panel.style.left = `${box.left}px`
  panel.style.top = `${box.top}px`
  panel.style.width = `${box.width}px`
  panel.style.maxHeight = `${box.maxHeight}px`

  const rect = panel.getBoundingClientRect()
  const dx = box.left - rect.left
  const dy = box.top - rect.top
  if (Math.abs(dx) > 0.5) panel.style.left = `${box.left + dx}px`
  if (Math.abs(dy) > 0.5) panel.style.top = `${box.top + dy}px`
}

// A small popover hung beside a badge — the DLC pack flyout. Prefers the
// badge's right, flips to its left when that would overflow, and clamps
// vertically against its own measured height.
export function placeBesideBadge(
  badge,
  { width, height, margin = MARGIN, gap = 10 } = {},
) {
  const b = badge.getBoundingClientRect()
  let left = b.right + gap
  if (left + width > window.innerWidth - margin) left = b.left - width - gap

  return {
    left: Math.round(Math.max(margin, left)),
    top: Math.round(
      Math.min(
        Math.max(margin, b.top - 4),
        Math.max(margin, window.innerHeight - height - margin),
      ),
    ),
  }
}
